import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { getAttribution, getReferralCode, clearReferralCode } from '../utils/attribution';
import { clarityIdentify, clarityTags, minutesBucket } from '../utils/clarity';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('auth_token'));
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // On mount, verify the stored token is still valid
  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const { data } = await api.get('/api/auth/me');
        setUser(data.user);
      } catch {
        // Token expired or invalid — clear everything
        localStorage.removeItem('auth_token');
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    verify();
  }, []);

  // Mid-session token death: api.js fires this when a 401's refresh attempt
  // also fails. Clearing user/token here makes PrivateRoute redirect to /login
  // immediately instead of leaving a zombie logged-in UI whose requests all 401.
  useEffect(() => {
    const onSessionExpired = () => {
      setToken(null);
      setUser(null);
    };
    window.addEventListener('auth:session-expired', onSessionExpired);
    return () => window.removeEventListener('auth:session-expired', onSessionExpired);
  }, []);

  // Make the session findable in Clarity: who it is (opaque id, never the email)
  // and what they are working with. Only /me and refreshUser carry `subscription`,
  // so the balance tags fill in a moment after the thinner login payload.
  useEffect(() => {
    if (!user) return;
    clarityIdentify(user.id);
    const sub = user.subscription;
    clarityTags({
      plan: sub?.plan,
      role: user.role,
      verified: String(Boolean(user.emailVerified)),
      // Bucketed, not exact: what matters when replaying is "did they have room
      // left", and a bucket stays a usable filter as the balance ticks down.
      minutesLeft: sub ? minutesBucket(sub.secondsBalance) : undefined,
    });
  }, [user]);

  const storeToken = (newToken, userData) => {
    localStorage.setItem('auth_token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  const login = async (email, password) => {
    const { data } = await api.post('/api/auth/login', { email, password });
    storeToken(data.token, data.user);
  };

  // Registration no longer logs the user in — the account stays inactive until
  // the emailed verification link is clicked. Returns the API payload so the
  // caller can show a "check your email" confirmation.
  const register = async (email, password, confirmPassword) => {
    // Replay the landing visit's campaign data — this request is the only point
    // where it can be tied to an account.
    const { data } = await api.post('/api/auth/register', {
      email, password, confirmPassword, attribution: getAttribution(), referralCode: getReferralCode(),
    });
    // The code has done its job: a second account from this browser is not a
    // second referral.
    clearReferralCode();
    return data;
  };

  const resendVerification = async (email) => {
    const { data } = await api.post('/api/auth/resend-verification', { email });
    return data;
  };

  // Confirm an email from the link; the backend logs the user in on success.
  const verifyEmail = async (verifyToken) => {
    const { data } = await api.post(`/api/auth/verify-email/${verifyToken}`);
    if (data.token) storeToken(data.token, data.user);
    return data;
  };

  // Set a new password from an emailed reset link. The backend rotates all
  // sessions and logs the user straight in, so store the returned token.
  const resetPassword = async (resetToken, password) => {
    const { data } = await api.put(`/api/auth/reset-password/${resetToken}`, { password });
    if (data.token) storeToken(data.token, data.user);
    return data;
  };

  const googleLogin = async (token) => {
    // Google sign-in doubles as sign-up, so it carries attribution too. The
    // server ignores it for an account that already exists.
    const { data } = await api.post('/api/auth/google', { token, attribution: getAttribution(), referralCode: getReferralCode() });
    clearReferralCode();
    storeToken(data.token, data.user);
  };

  // Adopt a session issued outside the normal login call (desktop pairing
  // exchange). The API already set the refresh cookie on that response.
  const adoptSession = (data) => storeToken(data.token, data.user);

  // Resolves to the fresh user (or null when the refresh fails), for callers that need the
  // up-to-date account right away, e.g. a purchase made in another tab.
  const refreshUser = async () => {
    try {
      const { data } = await api.get('/api/auth/me');
      setUser(data.user);
      return data.user;
    } catch {
      // Silently ignore — user state stays as-is if the refresh fails
      return null;
    }
  };

  const logout = async () => {
    try {
      await api.post('/api/auth/logout');
    } catch {
      // Ignore logout errors — clear local state regardless
    } finally {
      localStorage.removeItem('auth_token');
      setToken(null);
      setUser(null);
      // Land on the home page instead of letting PrivateRoute bounce to /login
      navigate('/', { replace: true });
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, resendVerification, verifyEmail, resetPassword, googleLogin, adoptSession, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
